import { useState, useEffect, useMemo } from 'react';

// Listing SEO Tips with completion checks
const LISTING_SEO_TIPS = {
  description_thin: {
    title: 'Description is very thin',
    tips: [
      'Write at least 300 characters describing your services',
      'Include your main skills and expertise areas',
      'Mention your experience and qualifications',
      'Add information about your availability and rates',
      'Use natural language that includes your target keywords'
    ]
  },
  description_short: {
    title: 'Description needs more detail',
    tips: [
      'Expand your current description to 300+ characters',
      'Add specific details about your services',
      'Include client benefits and unique selling points',
      'Mention any certifications or special skills'
    ]
  },
  images_missing: {
    title: 'No images added',
    tips: [
      'Add at least 1 high-quality profile photo',
      'Use clear, professional images',
      'Optimal image size is 800x800 pixels or larger',
      'Images help build trust with potential clients'
    ]
  },
  images_few: {
    title: 'Add more images',
    tips: [
      'Profiles with 5+ images get more engagement',
      'Show different aspects of your work or personality',
      'Include portfolio samples if applicable',
      'Add images with good lighting and resolution'
    ]
  },
  keywords_missing: {
    title: 'Add SEO keywords',
    tips: [
      'Open SEO Settings section at the bottom of the form',
      'Add 3-5 relevant keywords for your services',
      'Use keywords that clients might search for',
      'Include location-based keywords if relevant'
    ]
  },
  keywords_usage: {
    title: 'Use keywords in content',
    tips: [
      'Include your SEO keywords naturally in your description',
      'Don\'t stuff keywords - use them naturally',
      'Keywords should appear in title or description',
      'Each keyword should be used at least once'
    ]
  },
  skills_few: {
    title: 'Add more skills/services',
    tips: [
      'List at least 3 relevant skills',
      'Choose services that match your expertise',
      'More skills help you appear in more searches',
      'Be specific about your capabilities'
    ]
  },
  meta_title: {
    title: 'Add custom SEO title',
    tips: [
      'Create a unique title (30-60 characters)',
      'Include your main service and location',
      'Example: "Professional [Service] in [City]"',
      'This appears in search engine results'
    ]
  },
  meta_description: {
    title: 'Add meta description',
    tips: [
      'Write 100-160 characters summarizing your profile',
      'Include a call-to-action',
      'Mention your key differentiators',
      'This appears below the title in search results'
    ]
  },
  gender_missing: {
    title: 'Select a gender',
    tips: [
      'Complete your profile by selecting gender',
      'This helps with search filtering',
      'Improves profile completeness score'
    ]
  }
};

// Blog SEO Tips
const BLOG_SEO_TIPS = {
  content_thin: { 
    title: 'Content is very thin', 
    tips: ['Write at least 500 characters', 'Add introduction, body, and conclusion', 'Include relevant examples and details', 'Break content into paragraphs'] 
  },
  content_short: { 
    title: 'Add more content', 
    tips: ['Expand to 1500+ characters for best results', 'Add more sections or examples', 'Include helpful tips and insights'] 
  },
  image_missing: { 
    title: 'Add a featured image', 
    tips: ['Upload an eye-catching cover image', 'Use 1200x630 pixels for optimal sharing', 'Choose an image that represents the content'] 
  },
  image_alt: { 
    title: 'Add image alt text', 
    tips: ['Describe the image in 5-10 words', 'Include relevant keywords', 'Alt text improves SEO and accessibility'] 
  },
  meta_title: { 
    title: 'Add custom SEO title', 
    tips: ['Create a 30-60 character title', 'Include your main keyword', 'Make it compelling for clicks'] 
  },
  meta_desc: { 
    title: 'Add meta description', 
    tips: ['Write 100-160 characters', 'Summarize the post content', 'Include a call-to-action'] 
  },
  keywords: { 
    title: 'Add SEO keywords', 
    tips: ['Open SEO Settings section', 'Add 3-5 relevant keywords', 'Use keywords naturally in content'] 
  },
  tags_few: { 
    title: 'Add more tags', 
    tips: ['Add at least 3 relevant tags', 'Use descriptive tags', 'Tags help categorize content'] 
  },
  excerpt_short: { 
    title: 'Add longer excerpt', 
    tips: ['Write 100+ characters', 'Summarize the main point', 'This appears in blog listings'] 
  }
};

/**
 * Calculate SEO score for listings
 * @param {Object} data - Listing form data
 * @returns {Object} - { score, suggestions, completed }
 */
export const calculateListingSeoScore = (data) => {
  let score = 0;
  const suggestions = [];
  const completed = [];
  
  const seo = data.seo || {};
  const keywords = seo.keywords || [];
  const description = data.description || '';
  const title = data.title || '';
  const name = data.name || '';
  const images = data.images || [];
  const skills = data.skills || [];
  const services = data.services || [];
  
  // 1. Description length (0-25 points)
  const descLen = description.length;
  if (descLen >= 300) {
    score += 25;
    completed.push({ key: 'description', title: 'Description is detailed (300+ chars)', points: 25, is_complete: true });
  } else if (descLen >= 200) {
    score += 20;
    completed.push({ key: 'description', title: 'Description added (200+ chars)', points: 20, is_complete: true });
    suggestions.push({ key: 'description_short', ...LISTING_SEO_TIPS.description_short, points: 5, is_complete: false });
  } else if (descLen >= 100) {
    score += 15;
    suggestions.push({ key: 'description_short', ...LISTING_SEO_TIPS.description_short, points: 10, is_complete: false });
  } else if (descLen >= 50) {
    score += 10;
    suggestions.push({ key: 'description_short', ...LISTING_SEO_TIPS.description_short, points: 15, is_complete: false });
  } else {
    score += 5;
    suggestions.push({ key: 'description_thin', ...LISTING_SEO_TIPS.description_thin, points: 20, is_complete: false });
  }
  
  // 2. Image count (0-20 points)
  const imgCount = images.length;
  if (imgCount >= 5) {
    score += 20;
    completed.push({ key: 'images', title: '5+ images added', points: 20, is_complete: true });
  } else if (imgCount >= 3) {
    score += 15;
    completed.push({ key: 'images', title: '3+ images added', points: 15, is_complete: true });
    suggestions.push({ key: 'images_few', ...LISTING_SEO_TIPS.images_few, points: 5, is_complete: false });
  } else if (imgCount >= 1) {
    score += 10;
    completed.push({ key: 'images', title: 'Image added', points: 10, is_complete: true });
    suggestions.push({ key: 'images_few', ...LISTING_SEO_TIPS.images_few, points: 10, is_complete: false });
  } else {
    suggestions.push({ key: 'images_missing', ...LISTING_SEO_TIPS.images_missing, points: 20, is_complete: false });
  }
  
  // 3. Keywords presence (0-25 points)
  if (keywords.length > 0) {
    const content = `${name} ${title} ${description}`.toLowerCase();
    let keywordsFound = 0;
    keywords.forEach(kw => {
      if (content.includes(kw.toLowerCase())) keywordsFound++;
    });
    if (keywordsFound > 0) {
      const ratio = keywordsFound / keywords.length;
      const pts = Math.round(25 * ratio);
      score += pts;
      completed.push({ key: 'keywords', title: `Keywords added (${keywordsFound}/${keywords.length} used)`, points: pts, is_complete: ratio >= 0.5 });
      if (ratio < 0.5) {
        suggestions.push({ key: 'keywords_usage', ...LISTING_SEO_TIPS.keywords_usage, points: Math.round(25 * (1 - ratio)), is_complete: false });
      }
    }
  } else {
    score += 10;
    suggestions.push({ key: 'keywords_missing', ...LISTING_SEO_TIPS.keywords_missing, points: 15, is_complete: false });
  }
  
  // 4. Profile completeness (0-15 points)
  let completeness = 0;
  if (data.gender) {
    completeness += 3;
    completed.push({ key: 'gender', title: 'Gender selected', points: 3, is_complete: true });
  } else {
    suggestions.push({ key: 'gender_missing', ...LISTING_SEO_TIPS.gender_missing, points: 3, is_complete: false });
  }
  if (data.city) {
    completeness += 3;
    completed.push({ key: 'city', title: 'City selected', points: 3, is_complete: true });
  }
  if (data.category) {
    completeness += 3;
    completed.push({ key: 'category', title: 'Category selected', points: 3, is_complete: true });
  }
  if (data.phone) {
    completeness += 3;
    completed.push({ key: 'phone', title: 'Phone added', points: 3, is_complete: true });
  }
  if (skills.length >= 3 || services.length >= 2) {
    completeness += 3;
    completed.push({ key: 'skills', title: 'Skills/services added', points: 3, is_complete: true });
  } else {
    suggestions.push({ key: 'skills_few', ...LISTING_SEO_TIPS.skills_few, points: 3, is_complete: false });
  }
  score += completeness;
  
  // 5. SEO metadata (0-15 points)
  let seoMeta = 0;
  const metaTitle = seo.meta_title || '';
  const metaDesc = seo.meta_description || '';
  
  if (metaTitle && metaTitle.length >= 30) {
    seoMeta += 5;
    completed.push({ key: 'meta_title', title: 'SEO title added', points: 5, is_complete: true });
  } else if (!metaTitle) {
    suggestions.push({ key: 'meta_title', ...LISTING_SEO_TIPS.meta_title, points: 5, is_complete: false });
  }
  
  if (metaDesc && metaDesc.length >= 100) {
    seoMeta += 5;
    completed.push({ key: 'meta_desc', title: 'Meta description added', points: 5, is_complete: true });
  } else if (!metaDesc) {
    suggestions.push({ key: 'meta_description', ...LISTING_SEO_TIPS.meta_description, points: 5, is_complete: false });
  }
  
  if (seo.slug) {
    seoMeta += 3;
    completed.push({ key: 'slug', title: 'Custom slug added', points: 3, is_complete: true });
  }
  if (seo.og_image) {
    seoMeta += 2;
    completed.push({ key: 'og_image', title: 'OG image set', points: 2, is_complete: true });
  }
  score += seoMeta;
  
  return { score: Math.min(score, 100), suggestions, completed };
};

/**
 * Calculate SEO score for blog posts
 * @param {Object} data - Blog post form data
 * @returns {Object} - { score, suggestions, completed }
 */
export const calculateBlogSeoScore = (data) => {
  let score = 0;
  const suggestions = [];
  const completed = [];
  
  const content = data.content || '';
  const excerpt = data.excerpt || '';
  const featuredImage = data.featured_image || '';
  const featuredImageAlt = data.featured_image_alt || '';
  const tags = data.tags ? (typeof data.tags === 'string' ? data.tags.split(',').filter(Boolean) : data.tags) : [];
  const seo = data.seo || {};
  
  // 1. Content length (0-25)
  const contentLen = content.length;
  if (contentLen >= 1500) {
    score += 25;
    completed.push({ key: 'content', title: 'Content is detailed (1500+ chars)', points: 25, is_complete: true });
  } else if (contentLen >= 1000) {
    score += 20;
    completed.push({ key: 'content', title: 'Content added (1000+ chars)', points: 20, is_complete: true });
    suggestions.push({ key: 'content_short', ...BLOG_SEO_TIPS.content_short, is_complete: false });
  } else if (contentLen >= 500) {
    score += 15;
    suggestions.push({ key: 'content_short', ...BLOG_SEO_TIPS.content_short, is_complete: false });
  } else if (contentLen >= 200) {
    score += 10;
    suggestions.push({ key: 'content_short', ...BLOG_SEO_TIPS.content_short, is_complete: false });
  } else {
    score += 5;
    suggestions.push({ key: 'content_thin', ...BLOG_SEO_TIPS.content_thin, is_complete: false });
  }
  
  // 2. Featured image (0-15)
  if (featuredImage) {
    score += 10;
    completed.push({ key: 'image', title: 'Featured image added', points: 10, is_complete: true });
    if (featuredImageAlt) {
      score += 5;
      completed.push({ key: 'image_alt', title: 'Image alt text added', points: 5, is_complete: true });
    } else {
      suggestions.push({ key: 'image_alt', ...BLOG_SEO_TIPS.image_alt, is_complete: false });
    }
  } else {
    suggestions.push({ key: 'image_missing', ...BLOG_SEO_TIPS.image_missing, is_complete: false });
  }
  
  // 3. Meta title/description (0-20)
  const metaTitle = seo.title || '';
  const metaDesc = seo.description || '';
  if (metaTitle && metaTitle.length >= 30) {
    score += 10;
    completed.push({ key: 'meta_title', title: 'SEO title added', points: 10, is_complete: true });
  } else {
    suggestions.push({ key: 'meta_title', ...BLOG_SEO_TIPS.meta_title, is_complete: false });
  }
  if (metaDesc && metaDesc.length >= 100) {
    score += 10;
    completed.push({ key: 'meta_desc', title: 'Meta description added', points: 10, is_complete: true });
  } else {
    suggestions.push({ key: 'meta_desc', ...BLOG_SEO_TIPS.meta_desc, is_complete: false });
  }
  
  // 4. Keywords/tags (0-20)
  if (seo.keywords) {
    score += 10;
    completed.push({ key: 'keywords', title: 'SEO keywords added', points: 10, is_complete: true });
  } else {
    suggestions.push({ key: 'keywords', ...BLOG_SEO_TIPS.keywords, is_complete: false });
  }
  if (tags.length >= 3) {
    score += 10;
    completed.push({ key: 'tags', title: '3+ tags added', points: 10, is_complete: true });
  } else if (tags.length >= 1) {
    score += 5;
    completed.push({ key: 'tags', title: 'Tags added', points: 5, is_complete: true });
    suggestions.push({ key: 'tags_few', ...BLOG_SEO_TIPS.tags_few, is_complete: false });
  } else {
    suggestions.push({ key: 'tags_few', ...BLOG_SEO_TIPS.tags_few, is_complete: false });
  }
  
  // 5. SEO completeness (0-20)
  if (seo.canonical_url) {
    score += 5;
    completed.push({ key: 'canonical', title: 'Canonical URL set', points: 5, is_complete: true });
  }
  if (seo.og_image) {
    score += 5;
    completed.push({ key: 'og_image', title: 'OG image set', points: 5, is_complete: true });
  }
  if (seo.robots && seo.robots !== 'noindex, nofollow') {
    score += 5;
    completed.push({ key: 'robots', title: 'Robots meta configured', points: 5, is_complete: true });
  }
  if (excerpt && excerpt.length >= 100) {
    score += 5;
    completed.push({ key: 'excerpt', title: 'Excerpt added (100+ chars)', points: 5, is_complete: true });
  } else {
    suggestions.push({ key: 'excerpt_short', ...BLOG_SEO_TIPS.excerpt_short, is_complete: false });
  }
  
  return { score: Math.min(score, 100), suggestions, completed };
};

/**
 * Custom hook for listing SEO score
 * @param {Object} formData - Form data to calculate score from
 * @param {boolean} isAdmin - Whether user is admin
 * @returns {Object} - { score, suggestions, completed, getScoreColor, getScoreBorderColor }
 */
export const useListingSeoScore = (formData, isAdmin = false) => {
  const [liveScore, setLiveScore] = useState({ score: 0, suggestions: [], completed: [] });
  
  useEffect(() => {
    if (isAdmin) {
      const result = calculateListingSeoScore(formData);
      setLiveScore(result);
    }
  }, [formData, isAdmin]);
  
  const getScoreColor = useMemo(() => (score) => {
    if (score >= 70) return 'bg-emerald-500 text-white';
    if (score >= 50) return 'bg-yellow-500 text-black';
    return 'bg-red-500 text-white';
  }, []);
  
  const getScoreBorderColor = useMemo(() => (score) => {
    if (score >= 70) return 'border-emerald-500/50';
    if (score >= 50) return 'border-yellow-500/50';
    return 'border-red-500/50';
  }, []);
  
  return { ...liveScore, getScoreColor, getScoreBorderColor };
};

/**
 * Custom hook for blog SEO score
 * @param {Object} formData - Form data to calculate score from
 * @returns {Object} - { score, suggestions, completed, getScoreColor, getScoreBorderColor }
 */
export const useBlogSeoScore = (formData) => {
  const [liveScore, setLiveScore] = useState({ score: 0, suggestions: [], completed: [] });
  
  useEffect(() => {
    const tagsArray = formData.tags ? 
      (typeof formData.tags === 'string' ? formData.tags.split(',').filter(Boolean) : formData.tags) 
      : [];
    const result = calculateBlogSeoScore({ ...formData, tags: tagsArray });
    setLiveScore(result);
  }, [formData]);
  
  const getScoreColor = useMemo(() => (score) => {
    if (score >= 70) return 'bg-emerald-500 text-white';
    if (score >= 50) return 'bg-yellow-500 text-black';
    return 'bg-red-500 text-white';
  }, []);
  
  const getScoreBorderColor = useMemo(() => (score) => {
    if (score >= 70) return 'border-emerald-500/50';
    if (score >= 50) return 'border-yellow-500/50';
    return 'border-red-500/50';
  }, []);
  
  return { ...liveScore, getScoreColor, getScoreBorderColor };
};

export default { useListingSeoScore, useBlogSeoScore, calculateListingSeoScore, calculateBlogSeoScore };
